import gameanalytics from 'gameanalytics';
import {APP_VERSION} from '../constants/version';
import {isEnvConfEnabled} from "../utils/env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../constants/env";

const GAME_KEY = process.env.REACT_APP_GAMEANALYTICS_KEY;
const SECRET_KEY = process.env.REACT_APP_GAMEANALYTICS_SECRET;

let initialized = false;

export function initGameAnalytics() {
  if (isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_DISABLE_GAME_ANALYTICS)) {
    return;
  }

  if (initialized) {
    return;
  }

  if (!GAME_KEY || !SECRET_KEY) {
    console.error('[GameAnalytics] Keys not configured. Skipping initialization.');
    return;
  }

  // console.log('[GameAnalytics] GAME_KEY', GAME_KEY[0]);
  // console.log('[GameAnalytics] SECRET_KEY', SECRET_KEY[0]);

  try {
    gameanalytics.GameAnalytics.setEnabledInfoLog(process.env.NODE_ENV !== 'production');
    gameanalytics.GameAnalytics.configureBuild(APP_VERSION);
    gameanalytics.GameAnalytics.initialize(GAME_KEY, SECRET_KEY);
    initialized = true;
    console.log('[GameAnalytics] Initialized successfully');
  } catch (error) {
    console.error('[GameAnalytics] Initialization failed:', error);
  }
}

export function isGameAnalyticsInitialized() {
  return initialized;
}

export function getGameAnalytics() {
  return gameanalytics.GameAnalytics;
}

/**
 * Клик по кнопке экрана.
 *
 * У гостя (неавторизованный посетитель, см. configs/guestProfile.js) метка
 * встает ОТДЕЛЬНЫМ уровнем сразу после экрана:
 *
 *   page:MainMenu:button:tutorial          — авторизованный
 *   page:MainMenu:guest:button:tutorial    — гость
 *
 * Так в статистике вся гостевая ветка экрана сворачивается в один узел и
 * агрегируется целиком, а не собирается по крупицам из хвостов событий.
 *
 * @param {string} pageId
 * @param {string} buttonId
 * @param {string} [buttonValue]
 * @param {Object} [options]
 * @param {boolean} [options.isGuest]
 */
export function sendButtonClickEvent(pageId, buttonId, buttonValue, {isGuest = false} = {}) {
  if (!isGameAnalyticsInitialized()) {
    return;
  }
  try {
    // Пять уровней — потолок дизайн-события GameAnalytics, и у гостя один из
    // них уже занят меткой: значение кнопки в этом случае не добавляем, иначе
    // SDK отбросит событие целиком по валидации. Кнопки, которым значение
    // важно, гостю недоступны — он до них просто не доходит
    const guestLevel = isGuest ? 'guest:' : '';
    const valueLevel = (!isGuest && buttonValue) ? `:${buttonValue}` : '';
    getGameAnalytics().addDesignEvent(`page:${pageId}:${guestLevel}button:${buttonId}${valueLevel}`);
  } catch (error) {
    console.error(`[sendButtonClickEvent] Failed to send event for ${pageId}:${buttonId}:`, error);
  }
}

/**
 * Событие сетевого слоя матча: вынужденное переподключение по тишине в канале,
 * запрос состояния из-за протухшего хода и т.п. Без него такие сбои полностью
 * невидимы — о них известно только со слов игроков.
 */
export function sendNetworkEvent(eventId, eventValue) {
  if (!isGameAnalyticsInitialized()) {
    return;
  }
  try {
    getGameAnalytics().addDesignEvent(`network:${eventId}${eventValue ? ':' + eventValue : ''}`);
  } catch (error) {
    console.error(`[sendNetworkEvent] Failed to send event ${eventId}:`, error);
  }
}

// Больше пяти уровней GameAnalytics не принимает и отбрасывает событие целиком
const MAX_EVENT_LEVELS = 5;

/**
 * Произвольное событие экрана: не клик, а факт того, что что-то произошло
 * (показ модалки, шаг сценария). Дополнительные уровни идут отдельными
 * аргументами и складываются в дерево — так статистика сворачивается по
 * каждому уровню: page:LoginRequiredModal:mount:shop:mobile
 *
 * @param {string} pageId
 * @param {string} eventId
 * @param {...(string|undefined)} levels - уточняющие уровни, пустые пропускаются
 */
export function sendPageEvent(pageId, eventId, ...levels) {
  if (!isGameAnalyticsInitialized()) {
    return;
  }
  try {
    const eventLevels = ['page', pageId, eventId, ...levels]
      .filter(Boolean)
      .slice(0, MAX_EVENT_LEVELS);
    getGameAnalytics().addDesignEvent(eventLevels.join(':'));
  } catch (error) {
    console.error(`[sendPageEvent] Failed to send event for ${pageId}:${eventId}:`, error);
  }
}

// Допустимая часть идентификатора дизайн-события по правилам SDK GameAnalytics
// (GAValidator.validateDesignEvent): 1-64 символа латиницы, цифр, пробелов и
// - _ . ( ) ! ? — с другим символом SDK молча выбрасывает событие
const DESIGN_EVENT_PART_REGEX = /^[A-Za-z0-9\s\-_.()!?]{1,64}$/;

export const checkIfValidDesignEventParts = (parts) => (
  Array.isArray(parts)
  && parts.length > 0
  && parts.length <= MAX_EVENT_LEVELS
  && parts.every((part) => typeof part === 'string' && DESIGN_EVENT_PART_REGEX.test(part))
);

/**
 * Событие прохождения обучения: tutorial:<chapterId>:<eventId>:<уровни>
 * (идентификаторы — utils/tutorial/tutorialAnalyticsIds).
 *
 * В отличие от sendPageEvent пустые уровни не выкидываются: потерянный номер
 * шага сдвинул бы признак первого прохождения на его место, и событие молча
 * легло бы в чужой узел воронки. Такое событие не отправляется вовсе.
 *
 * @param {string} chapterId
 * @param {string} eventId
 * @param {...string} levels
 */
export function sendTutorialEvent(chapterId, eventId, ...levels) {
  const parts = ['tutorial', chapterId, eventId, ...levels];
  if (!checkIfValidDesignEventParts(parts)) {
    console.error('[sendTutorialEvent] Invalid tutorial event id:', parts);
    return;
  }
  if (!isGameAnalyticsInitialized()) {
    return;
  }
  try {
    getGameAnalytics().addDesignEvent(parts.join(':'));
  } catch (error) {
    console.error(`[sendTutorialEvent] Failed to send event ${parts.join(':')}:`, error);
  }
}

/**
 * Событие жизненного цикла приложения: воронка загрузки до первого экрана.
 *
 * Нужна, чтобы отличать «человек увидел интерфейс и ушел» от «интерфейс так и
 * не появился». Уникальные пользователи GameAnalytics на этот вопрос не
 * отвечают: счетчик срабатывает при инициализации SDK в index.js, то есть
 * когда бандл всего лишь выполнился — упал ли React дальше, завис ли на
 * сплеше, из статистики не видно.
 */
export function sendAppEvent(eventId, eventValue) {
  if (!isGameAnalyticsInitialized()) {
    return;
  }
  try {
    getGameAnalytics().addDesignEvent(`app:${eventId}${eventValue ? ':' + eventValue : ''}`);
  } catch (error) {
    console.error(`[sendAppEvent] Failed to send event ${eventId}:`, error);
  }
}

// Значения enum EGAErrorSeverity из SDK. Берем их через сам SDK, но с
// числовым запасом: в рантайме это обычные числа, и потеря enum при смене
// сборки пакета не должна ронять отправку ошибок
const getErrorSeverity = (name, fallbackValue) => (
  gameanalytics?.EGAErrorSeverity?.[name] ?? fallbackValue
);

// Один и тот же сбой в цикле рендера способен выстрелить сотнями событий —
// шлем ограниченное число уникальных сообщений за загрузку страницы
const MAX_REPORTED_ERRORS = 10;
const reportedErrorMessages = new Set();

/**
 * Ошибка выполнения — в GameAnalytics. Без этого падения на проде полностью
 * невидимы: у игрока белый экран, а в статистике обычный «пользователь».
 * @param {string} message
 * @param {boolean} [isCritical] - критично (упал рендер) или просто ошибка
 */
export function sendErrorEvent(message, isCritical = false) {
  if (!isGameAnalyticsInitialized() || !message) {
    return;
  }
  // Сообщения бывают длинными (стек в тексте) — режем, иначе SDK отбросит
  // событие целиком по валидации
  const shortMessage = String(message).slice(0, 200);
  if (reportedErrorMessages.has(shortMessage) || reportedErrorMessages.size >= MAX_REPORTED_ERRORS) {
    return;
  }
  reportedErrorMessages.add(shortMessage);

  try {
    const severity = isCritical
      ? getErrorSeverity('Critical', 5)
      : getErrorSeverity('Error', 4);
    getGameAnalytics().addErrorEvent(severity, shortMessage);
  } catch (error) {
    console.error('[sendErrorEvent] Failed to send error event:', error);
  }
}

/**
 * Глобальные ловушки ошибок. Вызывается один раз при старте (см. index.js)
 * сразу после инициализации SDK.
 */
export function initErrorReporting() {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', (event) => {
    // Ошибки загрузки ресурсов (картинка, скрипт) приходят сюда же, но без
    // message — их отмечаем отдельно, они не про сбой кода
    const message = event.message
      || (event.target?.src ? `resource failed: ${event.target.src}` : null);
    sendErrorEvent(message);
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    sendErrorEvent(reason?.message || String(reason));
  });
}
